/**
 * Gear Builder (lite) — selectable set/icon rows for news articles.
 *
 * Author example:
 *
 *   <gear-builder>
 *   {
 *     "title": "Optional card title",
 *     "rows": [
 *       {
 *         "type": "set-list",
 *         "label": "Sets",
 *         "options": [
 *           { "name": "Wave", "data": ["Strength 123", "Agility 45"] },
 *           { "name": "Tidal", "data": "Strength 100" }
 *         ]
 *       },
 *       {
 *         "type": "icon-list",
 *         "label": "Accessories",
 *         "options": [
 *           { "icon": "/img/news/gear/wave-ring.png", "name": "Wave Ring", "data": "+12% Agility" }
 *         ]
 *       }
 *     ]
 *   }
 *   </gear-builder>
 */

export type GearBuilderDataValue = string | string[]

export interface GearBuilderSetOption {
  name: string
  data?: GearBuilderDataValue
}

export interface GearBuilderIconOption {
  /** Public path or absolute URL (e.g. `/img/news/gear/wave-ring.png`). */
  icon: string
  name: string
  data?: GearBuilderDataValue
}

export interface GearBuilderSetListRow {
  type: 'set-list'
  label?: string
  options: GearBuilderSetOption[]
}

export interface GearBuilderIconListRow {
  type: 'icon-list'
  label?: string
  options: GearBuilderIconOption[]
}

export type GearBuilderRow = GearBuilderSetListRow | GearBuilderIconListRow

export interface GearBuilderPayload {
  title?: string
  rows: GearBuilderRow[]
}

/** Normalize `data` (string or string[]) into display lines. */
export function normalizeGearBuilderData(data: GearBuilderDataValue | undefined | null): string[] {
  if (data == null) return []
  if (Array.isArray(data)) {
    return data.map((line) => String(line).trim()).filter(Boolean)
  }
  const text = String(data).trim()
  if (!text) return []
  return text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function parseSetOption(raw: unknown): GearBuilderSetOption | null {
  if (!isRecord(raw)) return null
  const name = typeof raw.name === 'string' ? raw.name.trim() : ''
  if (!name) return null
  const data = raw.data
  if (data == null || typeof data === 'string' || Array.isArray(data)) {
    return { name, data: data as GearBuilderDataValue | undefined }
  }
  return { name }
}

function parseIconOption(raw: unknown): GearBuilderIconOption | null {
  if (!isRecord(raw)) return null
  const icon = typeof raw.icon === 'string' ? raw.icon.trim() : ''
  const name = typeof raw.name === 'string' ? raw.name.trim() : ''
  if (!icon || !name) return null
  const data = raw.data
  if (data == null || typeof data === 'string' || Array.isArray(data)) {
    return { icon, name, data: data as GearBuilderDataValue | undefined }
  }
  return { icon, name }
}

function parseRow(raw: unknown): GearBuilderRow | null {
  if (!isRecord(raw)) return null
  const type = raw.type
  const label = typeof raw.label === 'string' ? raw.label.trim() : undefined
  const optionsRaw = Array.isArray(raw.options) ? raw.options : []

  if (type === 'set-list') {
    const options = optionsRaw.map(parseSetOption).filter((o): o is GearBuilderSetOption => !!o)
    if (!options.length) return null
    return { type: 'set-list', label: label || undefined, options }
  }

  if (type === 'icon-list') {
    const options = optionsRaw.map(parseIconOption).filter((o): o is GearBuilderIconOption => !!o)
    if (!options.length) return null
    return { type: 'icon-list', label: label || undefined, options }
  }

  return null
}

/** Parse encodeURIComponent JSON, raw JSON string, or already-parsed object. */
export function parseGearBuilderPayload(
  input: unknown,
): GearBuilderPayload | null {
  if (input == null || input === '') return null

  let value: unknown = input
  if (typeof input === 'string') {
    let text = input.trim()
    if (!text) return null

    // Preprocess stores JSON as encodeURIComponent for safe MDC props.
    if (!text.startsWith('{') && !text.startsWith('[')) {
      try {
        text = decodeURIComponent(text)
      } catch {
        // keep raw
      }
    }

    text = text.trim()
    if (text.startsWith('```')) {
      text = text.replace(/^```(?:json|JSON)?\s*/, '').replace(/\s*```$/, '').trim()
    }

    try {
      value = JSON.parse(text)
    } catch {
      return null
    }
  }

  if (!isRecord(value)) return null

  const title = typeof value.title === 'string' ? value.title.trim() : undefined
  const rowsRaw = Array.isArray(value.rows) ? value.rows : []
  const rows = rowsRaw.map(parseRow).filter((r): r is GearBuilderRow => !!r)
  if (!rows.length) return null

  return {
    title: title || undefined,
    rows,
  }
}
